(() => {
  const namespace = 'DeebCigarGalleryController';

  if (window[namespace]) {
    window[namespace].init(document);
    return;
  }

  const selectors = {
    gallery: '[data-deeb-cigar-gallery]',
    sortControl: '[data-deeb-sort]',
    sortNative: '[data-deeb-sort-native]',
    sortCurrent: '[data-deeb-sort-current]',
    sortDisclosure: '[data-deeb-sort-disclosure]',
    sortOption: '[data-deeb-sort-value]',
    reveal: '[data-deeb-reveal]',
    navigationList: '.deeb-cigar-gallery__navigation-list',
    navigationActive: '.deeb-cigar-gallery__navigation-link.is-active',
    progress: '[data-deeb-scroll-progress]',
    progressCigar: '[data-deeb-scroll-cigar]',
    progressEmber: '[data-deeb-scroll-ember]',
    mobileFacetForm: '#FacetFiltersFormMobile',
    mobileSortHost: '[id^="Details-Mobile-SortBy-"]',
    mobileSortSelect: '#SortBy-mobile',
  };

  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const mobileNavigationQuery = window.matchMedia('(max-width: 749px)');
  const mobileFacetsQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sortControls = new WeakSet();
  const navigationLists = new WeakSet();
  const mobileFacetControllers = new Map();
  const galleryControllers = new Map();
  const galleryHydrationObservers = new WeakMap();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const getElementTarget = (event) => (event.target instanceof Element ? event.target : null);

  const getSortParts = (control) => ({
    select: control.querySelector(selectors.sortNative),
    current: control.querySelector(selectors.sortCurrent),
    disclosure: control.querySelector(selectors.sortDisclosure),
    options: [...control.querySelectorAll(selectors.sortOption)],
  });

  const syncSortControl = (control) => {
    const { select, current, options } = getSortParts(control);
    if (!(select instanceof HTMLSelectElement)) return;

    const selectedOption = select.selectedOptions?.[0];
    if (current && selectedOption) current.textContent = selectedOption.textContent.trim();

    let selectedButton = null;
    options.forEach((option) => {
      const isSelected = option.dataset.deebSortValue === select.value;
      option.classList.toggle('is-selected', isSelected);
      option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      option.tabIndex = isSelected ? 0 : -1;
      if (isSelected) selectedButton = option;
    });

    if (!selectedButton && options[0]) options[0].tabIndex = 0;
  };

  const closeSortDisclosure = (disclosure, returnFocus = false) => {
    if (!(disclosure instanceof HTMLDetailsElement) || !disclosure.open) return;
    disclosure.open = false;
    if (returnFocus) disclosure.querySelector('summary')?.focus();
  };

  const focusRelativeSortOption = (control, direction) => {
    const { options } = getSortParts(control);
    if (!options.length) return;

    const currentIndex = options.indexOf(document.activeElement);
    let nextIndex = currentIndex;

    if (direction === 'first') nextIndex = 0;
    else if (direction === 'last') nextIndex = options.length - 1;
    else if (direction === 'next') nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % options.length;
    else if (direction === 'previous') {
      nextIndex = currentIndex < 0 ? options.length - 1 : (currentIndex - 1 + options.length) % options.length;
    }

    options[nextIndex]?.focus();
  };

  const initSortControl = (control) => {
    if (!(control instanceof HTMLElement) || sortControls.has(control)) return;

    const { select, disclosure } = getSortParts(control);
    if (!(select instanceof HTMLSelectElement) || !(disclosure instanceof HTMLDetailsElement)) return;

    sortControls.add(control);
    syncSortControl(control);

    control.addEventListener('click', (event) => {
      const target = getElementTarget(event);
      const option = target?.closest(selectors.sortOption);
      if (!(option instanceof HTMLButtonElement) || !control.contains(option)) return;

      const nextValue = option.dataset.deebSortValue;
      closeSortDisclosure(disclosure, true);

      if (!nextValue || select.value === nextValue) {
        syncSortControl(control);
        return;
      }

      select.value = nextValue;
      syncSortControl(control);
      select.dispatchEvent(new Event('input', { bubbles: true }));
    });

    control.addEventListener('keydown', (event) => {
      if (!disclosure.open) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeSortDisclosure(disclosure, true);
        return;
      }

      const directions = {
        ArrowDown: 'next',
        ArrowUp: 'previous',
        Home: 'first',
        End: 'last',
      };
      const direction = directions[event.key];
      if (!direction) return;

      event.preventDefault();
      focusRelativeSortOption(control, direction);
    });

    disclosure.addEventListener('toggle', () => {
      if (disclosure.open) syncSortControl(control);
    });

    select.addEventListener('change', () => syncSortControl(control));
  };

  const initSortControls = (scope = document) => {
    if (!(scope instanceof Element || scope instanceof Document || scope instanceof DocumentFragment)) return;

    if (scope instanceof Element && scope.matches(selectors.sortControl)) initSortControl(scope);
    scope.querySelectorAll?.(selectors.sortControl).forEach(initSortControl);
  };

  const excludedMobileSortValues = new Set(['title-ascending', 'title-descending']);
  const mobileFacetMutationSelector = [
    '.js-filter',
    '.mobile-facets__summary',
    '.mobile-facets__list',
    selectors.mobileSortHost,
    selectors.mobileSortSelect,
  ].join(',');

  const nodeTouchesMobileFacetStructure = (node) => {
    if (!(node instanceof Element)) return false;
    return node.matches(mobileFacetMutationSelector) || Boolean(node.querySelector(mobileFacetMutationSelector));
  };

  const mobileSortCloseTimers = new WeakMap();
  const mobileSortCommitTimers = new WeakMap();
  const mobileSortConfirmMs = 80;
  const mobileSortTransitionMs = 260;
  const scheduleMicrotask =
    typeof window.queueMicrotask === 'function'
      ? window.queueMicrotask.bind(window)
      : (callback) => Promise.resolve().then(callback);

  const getMobileSortPanel = (host) => {
    if (!(host instanceof HTMLElement)) return null;

    const panelId = host.dataset.deebMobileSortPanelId;
    if (!panelId) return null;

    const panel = document.getElementById(panelId);
    return panel instanceof HTMLElement ? panel : null;
  };

  const getMobileSortMain = (host) => host.closest('.mobile-facets__main');

  const submitMobileSort = (select) => {
    if (!(select instanceof HTMLSelectElement)) return;

    /*
     * The native Shopify select remains the single source of truth. Dawn owns
     * the form input listener, URL state, history and AJAX rendering.
     */
    scheduleMicrotask(() => {
      if (!select.isConnected) return;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      select.dispatchEvent(new Event('input', { bubbles: true }));
    });
  };

  const syncMobileSort = (host) => {
    const select = host.querySelector(selectors.mobileSortSelect);
    const current = host.querySelector('[data-deeb-mobile-sort-current]');
    const panel = getMobileSortPanel(host);
    if (!(select instanceof HTMLSelectElement)) return;

    const selected = [...select.options].find((option) => option.value === select.value);
    if (current && selected && current.textContent !== selected.textContent.trim()) {
      current.textContent = selected.textContent.trim();
    }

    panel?.querySelectorAll('[data-deeb-mobile-sort-value]').forEach((button) => {
      button.setAttribute('aria-checked', button.dataset.deebMobileSortValue === select.value ? 'true' : 'false');
    });
  };

  const finishMobileSortClose = (panel) => {
    if (!panel.classList.contains('is-open')) {
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
    }
    mobileSortCloseTimers.delete(panel);
  };

  const clearMobileSortCommit = (panel) => {
    const timers = mobileSortCommitTimers.get(panel);
    if (timers?.confirm) window.clearTimeout(timers.confirm);
    if (timers?.submit) window.clearTimeout(timers.submit);
    mobileSortCommitTimers.delete(panel);

    panel.classList.remove('is-committing');
    delete panel.dataset.deebMobileSortCommitting;
  };

  const setMobileSortOpen = (host, open, options = {}) => {
    const panel = getMobileSortPanel(host);
    const trigger = host.querySelector('[data-deeb-mobile-sort-trigger]');
    const main = getMobileSortMain(host);
    if (!(panel instanceof HTMLElement) || !(main instanceof HTMLElement)) return;

    const currentTimer = mobileSortCloseTimers.get(panel);
    if (currentTimer) {
      window.clearTimeout(currentTimer);
      mobileSortCloseTimers.delete(panel);
    }

    if (open) {
      clearMobileSortCommit(panel);
      main.querySelectorAll('[data-deeb-mobile-sort-panel].is-open').forEach((openPanel) => {
        if (openPanel === panel) return;
        openPanel.classList.remove('is-open');
        openPanel.hidden = true;
        openPanel.setAttribute('aria-hidden', 'true');
      });

      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      trigger?.setAttribute('aria-expanded', 'true');
      host.dataset.deebMobileSortOpen = 'true';
      main.classList.add('deeb-mobile-sort-open');

      requestAnimationFrame(() => {
        panel.classList.add('is-open');
        if (options.focus !== false) {
          panel
            .querySelector('[data-deeb-mobile-sort-value][aria-checked="true"]')
            ?.focus({ preventScroll: true });
        }
      });
      return;
    }

    if (!options.preserveCommit) clearMobileSortCommit(panel);

    panel.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
    delete host.dataset.deebMobileSortOpen;
    main.classList.remove('deeb-mobile-sort-open');

    if (options.immediate || reducedMotionQuery.matches) finishMobileSortClose(panel);
    else {
      mobileSortCloseTimers.set(
        panel,
        window.setTimeout(() => finishMobileSortClose(panel), mobileSortTransitionMs + 20),
      );
    }

    if (options.returnFocus) trigger?.focus({ preventScroll: true });
  };

  const commitMobileSort = (host, panel, select, changed) => {
    clearMobileSortCommit(panel);

    panel.classList.add('is-committing');
    panel.dataset.deebMobileSortCommitting = 'true';

    const timers = {};
    const confirmDelay = reducedMotionQuery.matches ? 0 : mobileSortConfirmMs;
    const submitDelay = reducedMotionQuery.matches ? 0 : mobileSortTransitionMs + 20;

    timers.confirm = window.setTimeout(() => {
      if (!panel.isConnected || !host.isConnected) {
        clearMobileSortCommit(panel);
        return;
      }

      setMobileSortOpen(host, false, { preserveCommit: true });
      timers.submit = window.setTimeout(() => {
        clearMobileSortCommit(panel);
        if (changed) submitMobileSort(select);
      }, submitDelay);
    }, confirmDelay);

    mobileSortCommitTimers.set(panel, timers);
  };

  const stopOwnedClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  };

  const removeMobileSortEnhancement = (host) => {
    host.querySelector('[data-deeb-mobile-sort]')?.remove();
    const panel = getMobileSortPanel(host);
    if (panel) {
      clearMobileSortCommit(panel);
      panel.remove();
    }
    host.classList.remove('is-deeb-sort-enhanced');
    delete host.dataset.deebMobileSortPanelId;
    delete host.dataset.deebMobileSortOpen;
  };

  const buildMobileSort = (host) => {
    if (!(host instanceof HTMLElement)) return;

    const select = host.querySelector(selectors.mobileSortSelect);
    const main = getMobileSortMain(host);
    if (!(select instanceof HTMLSelectElement) || !(main instanceof HTMLElement)) return;

    const existingControl = host.querySelector('[data-deeb-mobile-sort]');
    const existingPanel = getMobileSortPanel(host);
    if (existingControl && existingPanel) {
      syncMobileSort(host);
      return;
    }

    removeMobileSortEnhancement(host);

    const options = [...select.options].filter((option) => !excludedMobileSortValues.has(option.value));
    if (!options.length) return;

    const ownerId = host.id || `DeebMobileSort-${Math.random().toString(36).slice(2)}`;
    if (!host.id) host.id = ownerId;
    const panelId = `${ownerId}-Panel`;

    const control = document.createElement('div');
    control.className = 'deeb-mobile-sort';
    control.dataset.deebMobileSort = '';
    control.innerHTML = `
      <button
        type="button"
        class="deeb-mobile-sort__trigger"
        data-deeb-mobile-sort-trigger
        aria-expanded="false"
        aria-controls="${panelId}"
      >
        <span class="deeb-mobile-sort__copy">
          <span class="deeb-mobile-sort__eyebrow">Sort by</span>
          <span class="deeb-mobile-sort__current" data-deeb-mobile-sort-current></span>
        </span>
        <span class="deeb-mobile-sort__arrow" aria-hidden="true"></span>
      </button>
    `;

    const panel = document.createElement('div');
    panel.id = panelId;
    panel.className = 'deeb-mobile-sort__panel';
    panel.dataset.deebMobileSortPanel = '';
    panel.dataset.deebMobileSortOwner = ownerId;
    panel.hidden = true;
    panel.setAttribute('aria-hidden', 'true');
    panel.innerHTML = `
      <button type="button" class="deeb-mobile-sort__back" data-deeb-mobile-sort-back>
        <span class="deeb-mobile-sort__back-icon" aria-hidden="true">←</span>
        <span>Sort by</span>
      </button>
      <div class="deeb-mobile-sort__options" role="radiogroup" aria-label="Sort products"></div>
    `;

    const list = panel.querySelector('.deeb-mobile-sort__options');
    options.forEach((option) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'deeb-mobile-sort__option';
      button.dataset.deebMobileSortValue = option.value;
      button.setAttribute('role', 'radio');
      button.innerHTML = '<span></span><span class="deeb-mobile-sort__radio" aria-hidden="true"></span>';
      button.firstElementChild.textContent = option.textContent.trim();

      button.addEventListener('click', (event) => {
        stopOwnedClick(event);

        const value = button.dataset.deebMobileSortValue;
        const changed = Boolean(value && select.value !== value);
        if (value) select.value = value;

        syncMobileSort(host);
        commitMobileSort(host, panel, select, changed);
      });

      list.append(button);
    });

    const trigger = control.querySelector('[data-deeb-mobile-sort-trigger]');
    const back = panel.querySelector('[data-deeb-mobile-sort-back]');

    trigger?.addEventListener('click', (event) => {
      stopOwnedClick(event);
      setMobileSortOpen(host, true);
    });

    back?.addEventListener('click', (event) => {
      stopOwnedClick(event);
      setMobileSortOpen(host, false, { returnFocus: true });
    });

    host.append(control);
    main.append(panel);
    host.dataset.deebMobileSortPanelId = panelId;
    host.classList.add('is-deeb-sort-enhanced');
    syncMobileSort(host);
  };

  const pruneMobileSortPanels = (form) => {
    const main = form.querySelector('.mobile-facets__main');
    if (!(main instanceof HTMLElement)) return;

    const validOwners = new Set(
      [...form.querySelectorAll(selectors.mobileSortHost)]
        .map((host) => host.id)
        .filter(Boolean),
    );

    main.querySelectorAll('[data-deeb-mobile-sort-panel]').forEach((panel) => {
      if (!validOwners.has(panel.dataset.deebMobileSortOwner || '')) panel.remove();
    });
  };

  const syncMobileFacetSummary = (details) => {
    if (!(details instanceof HTMLDetailsElement)) return;

    const copy = details.querySelector('.mobile-facets__summary > div');
    if (!(copy instanceof HTMLElement)) return;

    let meta = copy.querySelector('[data-deeb-mobile-facet-meta]');
    if (!meta) {
      meta = document.createElement('span');
      meta.className = 'deeb-mobile-facet__meta';
      meta.dataset.deebMobileFacetMeta = '';
      copy.append(meta);
    }

    const count = details.querySelectorAll('input[type="checkbox"]:checked').length;
    const nextText = count ? `${count} selected` : 'View options';
    if (meta.textContent !== nextText) meta.textContent = nextText;
  };

  const syncMobileFacetSummaries = (form) => {
    form.querySelectorAll('details.mobile-facets__details').forEach(syncMobileFacetSummary);
  };

  const syncMobileCheckboxImmediately = (input) => {
    if (!(input instanceof HTMLInputElement) || !input.matches('input.mobile-facets__checkbox[type="checkbox"]')) {
      return;
    }

    const label = input.closest('.mobile-facets__label');
    label?.classList.toggle('active', input.checked);
    syncMobileFacetSummary(input.closest('details.mobile-facets__details'));
  };

  class MobileFacetEnhancer {
    constructor(form) {
      this.form = form;
      this.mainDetails = form.closest('details');
      this.observer = 'MutationObserver' in window ? new MutationObserver((records) => this.onMutations(records)) : null;
      this.frame = 0;
      this.active = false;
      this.refreshCount = 0;
      this.handleInput = (event) => {
        const target = getElementTarget(event);
        if (target instanceof HTMLInputElement) syncMobileCheckboxImmediately(target);
        if (target instanceof HTMLSelectElement && target.matches(selectors.mobileSortSelect)) {
          const host = target.closest(selectors.mobileSortHost);
          if (host) syncMobileSort(host);
        }
      };
      this.handleKeydown = (event) => this.onKeydown(event);
      this.handleMainToggle = () => {
        if (!this.mainDetails?.open) {
          this.closeAllSortPanels({ immediate: true });
          return;
        }

        // Every fresh drawer opening starts from the filter-and-sort dashboard.
        this.closeAllSortPanels({ immediate: true });
      };
    }

    start() {
      if (this.active) {
        this.scheduleRefresh();
        return;
      }

      this.active = true;
      this.form.addEventListener('input', this.handleInput);
      this.form.addEventListener('keydown', this.handleKeydown);
      this.mainDetails?.addEventListener('toggle', this.handleMainToggle);
      this.refresh();
    }

    stop() {
      if (!this.active) return;

      this.active = false;
      this.form.removeEventListener('input', this.handleInput);
      this.form.removeEventListener('keydown', this.handleKeydown);
      this.mainDetails?.removeEventListener('toggle', this.handleMainToggle);
      this.observer?.disconnect();
      this.observer?.takeRecords();

      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.closeAllSortPanels({ immediate: true });
    }

    destroy() {
      this.stop();
      this.form.querySelectorAll(selectors.mobileSortHost).forEach(removeMobileSortEnhancement);
    }

    closeAllSortPanels(options = {}) {
      this.form.querySelectorAll(selectors.mobileSortHost).forEach((host) => {
        setMobileSortOpen(host, false, options);
      });
    }

    onKeydown(event) {
      if (event.key !== 'Escape') return;

      const openPanel = this.form.querySelector('[data-deeb-mobile-sort-panel].is-open');
      const ownerId = openPanel?.dataset.deebMobileSortOwner;
      const openHost = ownerId ? document.getElementById(ownerId) : null;
      if (!(openHost instanceof HTMLElement)) return;

      event.preventDefault();
      event.stopPropagation();
      setMobileSortOpen(openHost, false, { returnFocus: true });
    }

    observe() {
      if (!this.active || !this.observer || !this.form.isConnected) return;
      this.observer.observe(this.form, { childList: true, subtree: true });
    }

    refresh() {
      if (!this.active || !this.form.isConnected) return;

      /*
       * Dawn replaces mobile facet fragments after AJAX updates. Disconnecting
       * here is intentional: our progressive-enhancement DOM writes must never
       * feed back into this observer.
       */
      this.observer?.disconnect();
      this.observer?.takeRecords();

      pruneMobileSortPanels(this.form);
      this.form.querySelectorAll(selectors.mobileSortHost).forEach(buildMobileSort);
      syncMobileFacetSummaries(this.form);
      this.form.querySelectorAll('input.mobile-facets__checkbox[type="checkbox"]').forEach(syncMobileCheckboxImmediately);
      this.refreshCount += 1;

      this.observe();
    }

    scheduleRefresh() {
      if (!this.active || this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.refresh();
      });
    }

    onMutations(records) {
      if (!this.active || !mobileFacetsQuery.matches) return;

      const relevant = records.some((record) =>
        [...record.addedNodes, ...record.removedNodes].some(nodeTouchesMobileFacetStructure),
      );

      if (relevant) this.scheduleRefresh();
    }

    diagnostics() {
      return {
        active: this.active,
        connected: this.form.isConnected,
        refreshCount: this.refreshCount,
        pendingFrame: Boolean(this.frame),
        sortOpen: Boolean(this.form.querySelector('[data-deeb-mobile-sort-panel].is-open')),
        sortCommitting: Boolean(this.form.querySelector('[data-deeb-mobile-sort-panel].is-committing')),
      };
    }
  }

  const findMobileFacetForms = (scope = document) => {
    const forms = [];
    if (scope instanceof HTMLFormElement && scope.matches(selectors.mobileFacetForm)) forms.push(scope);
    scope.querySelectorAll?.(selectors.mobileFacetForm).forEach((form) => forms.push(form));
    return forms;
  };

  const initMobileFacets = (scope = document) => {
    findMobileFacetForms(scope).forEach((form) => {
      let controller = mobileFacetControllers.get(form);
      if (!controller) {
        controller = new MobileFacetEnhancer(form);
        mobileFacetControllers.set(form, controller);
      }

      if (mobileFacetsQuery.matches) controller.start();
      else controller.stop();
    });
  };

  const syncAllMobileFacetModes = () => {
    initMobileFacets(document);
    mobileFacetControllers.forEach((controller, form) => {
      if (!form.isConnected) {
        controller.destroy();
        mobileFacetControllers.delete(form);
        return;
      }

      if (mobileFacetsQuery.matches) controller.start();
      else controller.stop();
    });
  };

  const getMobileFacetDiagnostics = () =>
    [...mobileFacetControllers.values()].map((controller) => controller.diagnostics());

  const centerActiveNavigation = (list) => {
    if (!(list instanceof HTMLElement) || !mobileNavigationQuery.matches) return;

    const activeLink = list.querySelector(selectors.navigationActive);
    if (!(activeLink instanceof HTMLElement)) return;

    const left = activeLink.offsetLeft - (list.clientWidth - activeLink.offsetWidth) / 2;
    list.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
  };

  const initNavigationList = (list) => {
    if (!(list instanceof HTMLElement) || navigationLists.has(list)) return;

    navigationLists.add(list);
    requestAnimationFrame(() => centerActiveNavigation(list));
  };

  const initNavigationLists = (scope = document) => {
    if (!(scope instanceof Element || scope instanceof Document || scope instanceof DocumentFragment)) return;

    if (scope instanceof Element && scope.matches(selectors.navigationList)) initNavigationList(scope);
    scope.querySelectorAll?.(selectors.navigationList).forEach(initNavigationList);
  };

  const centerAllActiveNavigation = () => {
    document.querySelectorAll(selectors.navigationList).forEach(centerActiveNavigation);
  };

  /*
   * Dawn can replace gallery fragments after filtering/sorting on every
   * viewport. Structural rehydration must not depend on the optional
   * desktop reveal/scroll experience.
   */
  const hydrateGalleryContent = (scope) => {
    if (!(scope instanceof Element)) return;

    initSortControls(scope);
    initNavigationLists(scope);
    initMobileFacets(scope);

    document.dispatchEvent(
      new CustomEvent('deeb:wishlist:refresh', {
        detail: { scope },
      }),
    );
  };

  const observeGalleryHydration = (root) => {
    if (!(root instanceof Element) || galleryHydrationObservers.has(root) || !('MutationObserver' in window)) return;

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) hydrateGalleryContent(node);
        });
      });
    });

    observer.observe(root, { childList: true, subtree: true });
    galleryHydrationObservers.set(root, observer);
  };

  class GalleryExperience {
    constructor(root) {
      this.root = root;
      this.enabled = root.dataset.deebScrollExperience !== 'false';
      this.progress = root.querySelector(selectors.progress);
      this.progressCigar = root.querySelector(selectors.progressCigar);
      this.progressEmber = root.querySelector(selectors.progressEmber);
      this.revealObserver = null;
      this.mutationObserver = null;
      this.resizeObserver = null;
      this.frame = 0;
      this.metricsDirty = true;
      this.metrics = null;
      this.active = false;

      this.handleScroll = this.scheduleProgress.bind(this);
      this.handleResize = this.invalidateMetrics.bind(this);
      this.handleVisibility = this.onVisibilityChange.bind(this);
    }

    syncMode() {
      const shouldRun = this.enabled && desktopQuery.matches && !reducedMotionQuery.matches;
      if (shouldRun) this.start();
      else this.stop();
    }

    start() {
      if (this.active) {
        this.scheduleProgress();
        return;
      }

      this.active = true;
      this.metricsDirty = true;
      this.setupRevealObserver();
      this.setupMutationObserver();
      this.setupResizeObserver();
      this.prepareRevealTargets(this.root);

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibility);
      this.scheduleProgress();
    }

    stop() {
      if (!this.active) {
        this.revealAll();
        this.progress?.classList.remove('is-visible');
        return;
      }

      this.active = false;
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.revealObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.revealObserver = null;
      this.mutationObserver = null;
      this.resizeObserver = null;

      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.metrics = null;
      this.revealAll();
      this.progress?.classList.remove('is-visible');
      this.root.style.removeProperty('--deeb-scroll-remaining');
      this.root.style.removeProperty('--deeb-scroll-offset');
    }

    destroy() {
      this.stop();
    }

    setupRevealObserver() {
      if (!('IntersectionObserver' in window)) return;

      this.revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.revealTarget(entry.target);
            this.revealObserver?.unobserve(entry.target);
          });
        },
        { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.08 },
      );
    }

    setupMutationObserver() {
      if (!('MutationObserver' in window)) return;

      this.mutationObserver = new MutationObserver((records) => {
        let hasAddedContent = false;

        records.forEach((record) => {
          record.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            hasAddedContent = true;
            this.prepareRevealTargets(node);
          });
        });

        if (hasAddedContent) this.invalidateMetrics();
      });

      this.mutationObserver.observe(this.root, { childList: true, subtree: true });
    }

    setupResizeObserver() {
      if (!('ResizeObserver' in window)) return;

      this.resizeObserver = new ResizeObserver(() => this.invalidateMetrics());
      this.resizeObserver.observe(this.root);
    }

    prepareRevealTargets(scope) {
      if (!this.active) return;

      const targets = [];
      if (scope instanceof Element && scope.matches(selectors.reveal)) targets.push(scope);
      scope.querySelectorAll?.(selectors.reveal).forEach((target) => targets.push(target));

      targets.forEach((target) => {
        if (!(target instanceof HTMLElement) || target.dataset.deebRevealReady === 'true') return;

        target.dataset.deebRevealReady = 'true';
        const order = clamp(Number.parseInt(target.dataset.deebRevealOrder || '0', 10) || 0, 0, 4);
        target.style.setProperty('--deeb-reveal-delay', `${order * 45}ms`);

        const rect = target.getBoundingClientRect();
        const isInitiallyVisible = rect.bottom > 0 && rect.top < window.innerHeight * 0.9;
        if (isInitiallyVisible || !this.revealObserver) {
          this.revealTarget(target);
          return;
        }

        target.classList.add('deeb-reveal-pending');
        this.revealObserver.observe(target);
      });
    }

    revealTarget(target) {
      if (!(target instanceof HTMLElement)) return;
      target.classList.remove('deeb-reveal-pending');
      target.classList.add('is-revealed');
    }

    revealAll() {
      this.root.querySelectorAll(selectors.reveal).forEach((target) => {
        if (!(target instanceof HTMLElement)) return;
        target.classList.remove('deeb-reveal-pending');
        target.classList.add('is-revealed');
      });
    }

    invalidateMetrics() {
      this.metricsDirty = true;
      this.scheduleProgress();
    }

    onVisibilityChange() {
      if (!document.hidden) this.invalidateMetrics();
    }

    scheduleProgress() {
      if (!this.active || document.hidden || this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.updateProgress();
      });
    }

    refreshMetrics() {
      const rect = this.root.getBoundingClientRect();
      const pageTop = window.scrollY + rect.top;
      const height = this.root.offsetHeight;
      const viewportHeight = window.innerHeight;
      const distance = Math.max(1, height - viewportHeight);
      const cigarHeight = this.progressCigar?.clientHeight || 0;
      const emberHeight = this.progressEmber?.clientHeight || 0;

      this.metrics = {
        pageTop,
        pageBottom: pageTop + height,
        viewportHeight,
        distance,
        emberTravel: Math.max(0, cigarHeight - emberHeight),
      };
      this.metricsDirty = false;
    }

    updateProgress() {
      if (!this.progress || !this.progressCigar || !this.progressEmber) return;
      if (this.metricsDirty || !this.metrics) this.refreshMetrics();

      const scrollY = window.scrollY;
      const progress = clamp((scrollY - this.metrics.pageTop) / this.metrics.distance, 0, 1);
      const isGalleryVisible =
        scrollY + this.metrics.viewportHeight > this.metrics.pageTop && scrollY < this.metrics.pageBottom;

      this.root.style.setProperty('--deeb-scroll-remaining', (1 - progress).toFixed(4));
      this.root.style.setProperty('--deeb-scroll-offset', `${(progress * this.metrics.emberTravel).toFixed(2)}px`);
      this.progress.classList.toggle('is-visible', isGalleryVisible && this.metrics.distance > 1);
    }
  }

  const initGallery = (root) => {
    if (!(root instanceof HTMLElement)) return;

    let controller = galleryControllers.get(root);
    if (!controller) {
      controller = new GalleryExperience(root);
      galleryControllers.set(root, controller);
    }

    initSortControls(root);
    initNavigationLists(root);
    initMobileFacets(root);
    observeGalleryHydration(root);
    controller.syncMode();
  };

  const init = (scope = document) => {
    initSortControls(scope);
    initNavigationLists(scope);
    initMobileFacets(scope);

    if (scope instanceof Element && scope.matches(selectors.gallery)) initGallery(scope);
    scope.querySelectorAll?.(selectors.gallery).forEach(initGallery);
  };

  const closeOutsideSort = (event) => {
    const target = getElementTarget(event);
    document.querySelectorAll(`${selectors.sortDisclosure}[open]`).forEach((disclosure) => {
      if (!target || !disclosure.contains(target)) closeSortDisclosure(disclosure);
    });
  };

  const syncSortFromLocation = () => {
    const activeSort = new URLSearchParams(window.location.search).get('sort_by');

    document.querySelectorAll(selectors.sortControl).forEach((control) => {
      const { select } = getSortParts(control);
      if (!(select instanceof HTMLSelectElement)) return;

      if (activeSort && [...select.options].some((option) => option.value === activeSort)) {
        select.value = activeSort;
      }
      syncSortControl(control);
    });
  };

  const syncAllGalleryModes = () => {
    galleryControllers.forEach((controller) => controller.syncMode());
  };

  const destroyWithin = (scope) => {
    galleryControllers.forEach((controller, root) => {
      if (scope === root || (scope instanceof Element && scope.contains(root))) {
        controller.destroy();
        galleryHydrationObservers.get(root)?.disconnect();
        galleryHydrationObservers.delete(root);
        galleryControllers.delete(root);
      }
    });

    mobileFacetControllers.forEach((controller, form) => {
      if (scope === form || (scope instanceof Element && scope.contains(form))) {
        controller.destroy();
        mobileFacetControllers.delete(form);
      }
    });
  };

  window[namespace] = {
    init,
    syncSortControl,
    syncSortFromLocation,
    initMobileFacets,
    getMobileFacetDiagnostics,
  };

  document.addEventListener('click', closeOutsideSort);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  window.addEventListener('popstate', syncSortFromLocation);
  window.addEventListener('pageshow', () => {
    syncSortFromLocation();
    syncAllGalleryModes();
    syncAllMobileFacetModes();
    requestAnimationFrame(centerAllActiveNavigation);
  });
  desktopQuery.addEventListener?.('change', syncAllGalleryModes);
  mobileNavigationQuery.addEventListener?.('change', centerAllActiveNavigation);
  mobileFacetsQuery.addEventListener?.('change', syncAllMobileFacetModes);
  reducedMotionQuery.addEventListener?.('change', syncAllGalleryModes);

  init(document);
})();

(() => {
  'use strict';

  const GLOBAL_KEY = '__deebCollectionCardQuantityV1';
  const ROOT_SELECTOR = '[data-deeb-card-quantity]';
  const GALLERY_SELECTOR = '[data-deeb-cigar-gallery]';

  const existingRuntime = window[GLOBAL_KEY];
  if (existingRuntime) {
    existingRuntime.refresh?.(document);
    return;
  }

  const controllers = new WeakMap();
  const galleryObservers = new WeakMap();

  const toInteger = (value, fallback) => {
    const parsed = Number.parseInt(String(value ?? ''), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  class CollectionCardQuantity {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('[data-deeb-card-quantity-input]');
      this.minus = root.querySelector('[data-deeb-card-quantity-minus]');
      this.plus = root.querySelector('[data-deeb-card-quantity-plus]');
      this.counts = Array.from(root.querySelectorAll('[data-deeb-card-quantity-count]'));

      if (!this.input || !this.minus || !this.plus || this.counts.length === 0) return;

      this.minimum = Math.max(1, toInteger(root.dataset.quantityMin, 1));
      this.step = Math.max(1, toInteger(root.dataset.quantityStep, 1));
      this.maximum = root.dataset.quantityMax
        ? Math.max(this.minimum, toInteger(root.dataset.quantityMax, this.minimum))
        : null;

      this.handleMinus = () => this.changeBy(-this.step);
      this.handlePlus = () => this.changeBy(this.step);

      this.minus.addEventListener('click', this.handleMinus);
      this.plus.addEventListener('click', this.handlePlus);
      this.sync(this.currentValue());
    }

    currentValue() {
      return toInteger(this.input?.value, this.minimum);
    }

    clamp(value) {
      const atLeastMinimum = Math.max(this.minimum, value);
      return this.maximum === null
        ? atLeastMinimum
        : Math.min(this.maximum, atLeastMinimum);
    }

    changeBy(delta) {
      if (!this.input || this.input.disabled) return;
      this.sync(this.clamp(this.currentValue() + delta));
    }

    sync(value) {
      const nextValue = this.clamp(value);
      this.input.value = String(nextValue);
      this.counts.forEach((count) => {
        count.textContent = String(nextValue);
      });
      this.minus.disabled = nextValue <= this.minimum;
      this.plus.disabled = this.maximum !== null && nextValue >= this.maximum;
    }

    destroy() {
      this.minus?.removeEventListener('click', this.handleMinus);
      this.plus?.removeEventListener('click', this.handlePlus);
    }
  }

  const initControl = (root) => {
    if (!(root instanceof Element) || controllers.has(root)) return;
    controllers.set(root, new CollectionCardQuantity(root));
  };

  const refreshControls = (scope) => {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) initControl(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initControl);
  };

  const observeGallery = (gallery) => {
    if (!(gallery instanceof Element) || galleryObservers.has(gallery) || !('MutationObserver' in window)) return;

    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node instanceof Element) refreshControls(node);
        });
      });
    });

    observer.observe(gallery, { childList: true, subtree: true });
    galleryObservers.set(gallery, observer);
  };

  const refresh = (scope = document) => {
    refreshControls(scope);

    if (scope instanceof Element && scope.matches(GALLERY_SELECTOR)) observeGallery(scope);
    scope.querySelectorAll?.(GALLERY_SELECTOR).forEach(observeGallery);
  };

  const destroyWithin = (scope) => {
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      controllers.get(scope)?.destroy();
      controllers.delete(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });

    const galleries = [];
    if (scope instanceof Element && scope.matches(GALLERY_SELECTOR)) galleries.push(scope);
    scope.querySelectorAll?.(GALLERY_SELECTOR).forEach((gallery) => galleries.push(gallery));

    galleries.forEach((gallery) => {
      galleryObservers.get(gallery)?.disconnect();
      galleryObservers.delete(gallery);
    });
  };

  window[GLOBAL_KEY] = {
    version: 1,
    refresh,
    destroyWithin,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true });
  } else {
    refresh(document);
  }

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
